import { Tooltip } from "@mui/material";
import { TABLE_TEXT_FIELD_MAX_LENGTH } from "../helpers/constants";
import { convertStringBoolValueToBool, sortArrayOfObjectsByOrderArray } from "../helpers/methods";

const TableService = {
    getHeaderLables: (fieldsConfig, hiddenColumns = [], columnOrders = undefined) => {
        const sortedFieldsConfing = columnOrders
            ? sortArrayOfObjectsByOrderArray(fieldsConfig, columnOrders)
            : fieldsConfig;

        return sortedFieldsConfing
            .filter(
                (headerConfig) =>
                    !hiddenColumns.includes(headerConfig.name) && !hiddenColumns.onlyData,
            )
            .map((headerConfig) => headerConfig.label);
    },

    getPreparedDataForBaseTable: (
        fieldsConfig,
        data,
        hiddenColumns,
        columnOrders,
        ...extraValueCallbackProps
    ) => {
        const sortedFieldsConfing = columnOrders
            ? sortArrayOfObjectsByOrderArray(fieldsConfig, columnOrders)
            : fieldsConfig;

        const preparedData = [];
        for (const rowData of data) {
            const rowTableData = [];
            for (const { getValueCallback, ...headerConfig } of sortedFieldsConfing) {
                if (!hiddenColumns.includes(headerConfig.name)) {
                    rowTableData.push(getValueCallback(rowData, ...extraValueCallbackProps));
                }
            }
            preparedData.push({
                rowId: rowData.id,
                data: rowTableData,
            });
        }
        return preparedData;
    },

    getHeadersConfigForCollapsibleTable: (
        fieldsConfig,
        filteringData,
        hiddenColumns,
        columnOrders,
    ) => {
        const sortedFieldsConfing = columnOrders
            ? sortArrayOfObjectsByOrderArray(fieldsConfig, columnOrders)
            : fieldsConfig;

        const preparedHeadersConfig = [];
        for (const { getValueCallback, ...headerConfig} of sortedFieldsConfing) {
            if (!hiddenColumns.includes(headerConfig.name) && !headerConfig.onlyData) {
                preparedHeadersConfig.push({
                    ...headerConfig,
                    fieldConfig:
                        filteringData && headerConfig.getFilterFieldConfigCallback
                            ? {...headerConfig.getFilterFieldConfigCallback(filteringData), ...headerConfig.fieldConfig}
                            : undefined,
                });
            }
        }
        return preparedHeadersConfig;
    },

    hideHiddenColumns: (fieldsConfig, hiddenColumns) => {
        return fieldsConfig.filter(({ name }) => {
            return !hiddenColumns.includes(name) && !name.onlyData;
        });
    },

    orderColumns: (fieldsConfig, columnOrders) => {
        return columnOrders
            ? sortArrayOfObjectsByOrderArray(fieldsConfig, columnOrders)
            : fieldsConfig;
    },

  getFieldsConfigForEditableTable: (
    fieldsConfig,
    data,
    filteringData,
    ...extraValueCallbackProps
  ) => {
    let preparedData = [];
    let index = 0;

    for (const rowData of data) {
      if (rowData) {
        let rowTableData = [];
        for (const {
          getValueCallback,
          getEditable,
          getAlert,
          getSucces,
          fieldSx,
          getFieldSxCallback,
          ...headerConfig
        } of fieldsConfig) {
          rowTableData.push({
            ...headerConfig,
            fieldSx: getFieldSxCallback
              ? getFieldSxCallback(rowData)
              : fieldSx
              ? fieldSx
              : undefined,
            fieldConfig:
              filteringData && headerConfig.getFilterFieldConfigCallback
                ? headerConfig.getFilterFieldConfigCallback(
                    filteringData,
                    rowData
                  )
                : undefined,
            value: getValueCallback(rowData, index, ...extraValueCallbackProps),
            valueAsCheckbox: headerConfig.valueAsCheckbox === true,
            style: headerConfig.style ? headerConfig.style : {},
            editable:
              (getEditable ? getEditable(rowData) : headerConfig.editable) ===
              true,
            maxTextLength: headerConfig.maxTextLength,
            index: index,
            showAlert: (getAlert ? getAlert(rowData) : false) === true,
            showSucces: (getSucces ? getSucces(rowData) : false) === true,
            is_primary: rowData.is_primary,
          });
        }

        preparedData.push({
          id: rowData.id,
          editable: rowData.editable !== undefined ? rowData.editable : true,
          data: rowTableData,
          fieldConfig: rowData.fieldConfig,
          style: rowData.style,
          selected: rowData.selected,
          isDeleted: rowData.isDeleted,
          editDate: rowData.editDate,
        });
        index = index + 1;
      }
    }
    return preparedData;
  },

    getPreparedDataForCollapsibleTable: (
        fieldsConfig,
        data,
        hiddenColumns,
        columnOrders,
        ...extraValueCallbackProps
    ) => {
        const sortedFieldsConfing = columnOrders
            ? sortArrayOfObjectsByOrderArray(fieldsConfig, columnOrders)
            : fieldsConfig;

        const preparedData = [];
        if (!data) {
            return preparedData;
        }
        for (const rowData of data) {
            const rowTableData = [];
            for (const { getValueCallback, ...headerConfig } of sortedFieldsConfing) {
                if (!hiddenColumns.includes(headerConfig.name)) {
                    rowTableData.push({
                        name: headerConfig.name,
                        cellStyle:
                            headerConfig.cellStyle instanceof Function
                                ? headerConfig.cellStyle(rowData)
                                : headerConfig.cellStyle,
                        onlyData: headerConfig.onlyData,
                        valueAsCheckbox: headerConfig.valueAsCheckbox === true,
                        maxTextLength: headerConfig.maxTextLength,
                        value: getValueCallback(rowData, ...extraValueCallbackProps),
                    });
                }
            }

            const leadStatusHighlight =
                rowData.lead &&
                rowData.lead.lead_status === "None" &&
                !rowData.lead.lead_seller?.user_id;

            preparedData.push({
                id: rowData.id,
                updated: rowData.updated,
                updatedInnerElementData: rowData.updatedInnerElementData,
                updatedSelectedInnerElement: rowData.updatedSelectedInnerElement,
                is_favorite: rowData.is_favorite,
                is_claim: rowData.is_claim,
                is_without_response: rowData.is_without_response,
                is_closed: rowData.is_closed,
                is_offer_cr_requested: rowData.is_offer_cr_requested
                    ? rowData.is_offer_cr_requested
                    : false,
                is_exceed: rowData.exceed ? rowData.exceed : false,
                selected: rowData.selected,
                leadStatusHighlight: leadStatusHighlight,
                hasChanges: rowData.has_changes,
                invoice_is_booked: rowData.invoice_is_booked,
                transfer_is_booked: rowData.transfer_is_booked,
                transfer_is_invoiced: rowData.transfer_is_invoiced,
                checkboxIsDisabled: rowData.transfer_is_allowed_to_book !== undefined
                    ? rowData.transfer_is_allowed_to_book === false 
                    : false,
                invoice_is_connected: rowData.invoice_is_connected,
                data: rowTableData,
            });
        }

        return preparedData;
    },

    getTableConfigsForTableColumnVisibilityDialog: (tableConfigs, columnOrders = {}) => {
        const resData = {};
        tableConfigs.forEach((tableConfig) => {
            resData[tableConfig.name] = sortArrayOfObjectsByOrderArray(
                tableConfig.config,
                columnOrders[tableConfig.name],
            )
                .filter((config) => !config.onlyData)
                .map((config) => ({
                    label: config.label,
                    name: config.name,
                    allowChangeVisibility: config.allowChangeVisibility !== false, // default true
                    defaultHidden: config.defaultHidden === true, // default false
                }));
        });
        return resData;
    },

    getTableExportHeaders: (fieldsConfig, hiddenColumns, columnOrders) => {
        const headers = [];
        if (fieldsConfig !== undefined && hiddenColumns !== undefined) {
            for (const header of TableService.getHeadersConfigForCollapsibleTable(
                fieldsConfig,
                undefined,
                hiddenColumns,
                columnOrders,
            )) {
                if (!header.excludedFromExport) {
                    headers.push({
                        field_name: header.other_export_field_name
                            ? header.other_export_field_name
                            : header.name,
                        label: header.label,
                    });
                }
            }
        }
        return headers;
    },

    getCellContent: (value, customMaxTextLength) => {
        const maxTextLength = customMaxTextLength || TABLE_TEXT_FIELD_MAX_LENGTH;
        return typeof value === "string" && value.length > maxTextLength ? (
            <Tooltip title={value}>{`${value.slice(0, maxTextLength)}...`}</Tooltip>
        ) : (
            value
        );
    },
};

export default TableService;
